import React, { useState, useEffect, useCallback } from 'react';
import { Package, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';

const Products = () => {
  // 4 Product images provided by user
  const productImages = [
    {
      src: 'https://customer-assets.emergentagent.com/job_product-slideshow/artifacts/1mchifw5_Catlogue-NO%20MOISTURE_page-0001.jpg',
      alt: 'NOMOISTURE calcium chloride container desiccant bag catalogue cover - export cargo moisture protection',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_product-slideshow/artifacts/hj9yor83_Catlogue-NO%20MOISTURE_page-0002.jpg',
      alt: 'NOMOISTURE 2kg calcium chloride container desiccant bag hanging in shipping container to prevent container rain',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_product-slideshow/artifacts/2esuyqbi_Catlogue-NO%20MOISTURE_page-0003.jpg',
      alt: 'NOMOISTURE silica gel pouches for humidity control and moisture damage control in export packaging',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_product-slideshow/artifacts/dnko0fgi_Catlogue-NO%20MOISTURE_page-0004.jpg',
      alt: 'NOMOISTURE desiccant product range - calcium chloride desiccant bags and silica gel pouches for exporters',
    },
  ];

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    dragFree: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section 
      id="products"
      style={{
        background: '#060913',
        padding: 'clamp(3rem, 8vw, 5rem) 1.5rem',
      }}
    >
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(236, 236, 117, 0.15)',
            border: '1px solid rgba(236, 236, 117, 0.3)',
            color: '#ECEC75',
            padding: '0.5rem 1.5rem',
            borderRadius: '50px',
            marginBottom: '1.5rem',
          }}>
            <Package size={20} />
            <span style={{ 
              fontSize: '0.875rem', 
              fontWeight: 600,
            }}>
              Our Products
            </span>
          </div>

          <h2 style={{
            fontFamily: "'Crimson Text', serif",
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 700,
            color: '#f1f5f9',
            marginBottom: '1rem',
          }}>
            NOMOISTURE Calcium Chloride Desiccant Bags &amp; Silica Gel Pouches
          </h2>

          <p style={{
            fontSize: 'clamp(1rem, 2vw, 1.125rem)',
            color: '#cbd5e1',
            maxWidth: '760px',
            margin: '0 auto',
            lineHeight: 1.8,
          }}>
            Browse our range of high-absorption calcium chloride container desiccants and silica gel
            pouches - built for export cargo humidity protection, moisture damage control and reliable
            humidity control across shipping, storage and export packaging.
          </p>
        </div>

        {/* Product Image Slider */}
        <div style={{
          position: 'relative',
          maxWidth: '900px',
          margin: '0 auto',
        }}>
          {/* Carousel Container */}
          <div 
            ref={emblaRef} 
            style={{ 
              overflow: 'hidden',
              borderRadius: '16px',
              background: '#121827',
              border: '1px solid rgba(236, 236, 117, 0.2)',
            }}
          >
            <div style={{ display: 'flex' }}>
              {productImages.map((image, index) => (
                <div
                  key={index}
                  style={{
                    flex: '0 0 100%',
                    minWidth: 0,
                  }}
                >
                  <img 
                    src={image.src}
                    alt={image.alt}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Desktop */}
          <button
            onClick={scrollPrev}
            aria-label="Previous slide"
            style={{
              position: 'absolute',
              left: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#ECEC75',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 10,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(236, 236, 117, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            }}
          >
            <ChevronLeft size={24} color="#0a0e1a" />
          </button>
          
          <button
            onClick={scrollNext}
            aria-label="Next slide"
            style={{
              position: 'absolute',
              right: '-20px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: '#ECEC75',
              border: 'none',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 10,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(236, 236, 117, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
            }}
          >
            <ChevronRight size={24} color="#0a0e1a" />
          </button>

          {/* Dots Indicator */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '1.5rem',
          }}>
            {productImages.map((_, index) => (
              <button
                key={index}
                onClick={() => emblaApi && emblaApi.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                style={{
                  width: selectedIndex === index ? '32px' : '12px',
                  height: '12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: selectedIndex === index ? '#ECEC75' : 'rgba(236, 236, 117, 0.3)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  padding: 0,
                }}
              />
            ))}
          </div>

          {/* Swipe hint for mobile */}
          <p style={{
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '0.875rem',
            marginTop: '1rem',
            display: 'none',
          }}
          className="swipe-hint"
          >
            Swipe to view more products
          </p>
        </div>

        {/* Product Summary Cards - SEO Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '1.5rem',
          marginTop: '3.5rem',
        }} className="product-summary-grid">
          <article style={{
            background: '#121827',
            border: '1px solid rgba(236, 236, 117, 0.15)',
            borderRadius: '12px',
            padding: '1.75rem',
          }}>
            <h3 style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#ECEC75',
              marginTop: 0,
              marginBottom: '0.75rem',
            }}>
              Calcium Chloride Container Desiccant Bags
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#cbd5e1',
              lineHeight: 1.75,
              margin: 0,
            }}>
              NOMOISTURE high-absorption calcium chloride desiccant bags hold up to 300% of their own
              weight in water - engineered to prevent container rain in ocean freight and deliver
              export cargo humidity protection across 20ft and 40ft shipping containers. Ideal for
              exporters shipping food, agro, textiles, machinery and FMCG on long-haul routes.
            </p>
          </article>

          <article style={{
            background: '#121827',
            border: '1px solid rgba(236, 236, 117, 0.15)',
            borderRadius: '12px',
            padding: '1.75rem',
          }}>
            <h3 style={{
              fontFamily: "'Crimson Text', serif",
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#ECEC75',
              marginTop: 0,
              marginBottom: '0.75rem',
            }}>
              Silica Gel Pouches for Export Packaging
            </h3>
            <p style={{
              fontSize: '1rem',
              color: '#cbd5e1',
              lineHeight: 1.75,
              margin: 0,
            }}>
              NOMOISTURE silica gel pouches deliver targeted humidity control and moisture damage
              control inside boxes, cartons and pallets. Available in industrial and food-contact
              compatible grades for electronics, pharmaceuticals, textiles, leather goods and premium
              export packaging - keeping products dry from warehouse to destination port.
            </p>
          </article>
        </div>
      </div>

      {/* Mobile-specific styles */}
      <style>{`
        @media (min-width: 768px) {
          .product-summary-grid {
            grid-template-columns: 1fr 1fr !important;
          }
        }
        @media (max-width: 768px) {
          .swipe-hint {
            display: block !important;
          }
          #products button[aria-label="Previous slide"],
          #products button[aria-label="Next slide"] {
            display: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Products;
